import * as React from "react";
import { useHistory, LinkProps } from "react-router-dom";

import { SearchTypes } from "../types";

import {
  OpportunitySearchQueryState,
  OrganisationSearchQueryState,
  useThunkDispatch,
  opportunityResultStore,
  organisationResultStore,
} from "@skv/state";

export interface VolunteerSearchLinkProps
  extends Omit<LinkProps, "css" | "to"> {
  to?: Exclude<LinkProps["to"], Function>;
  searchQuery: OpportunitySearchQueryState | OrganisationSearchQueryState;
  searchType?: SearchTypes;
  shouldUpdateSearchForm?: boolean;
}

export const VolunteerSearchLink: React.FC<VolunteerSearchLinkProps> = ({
  to,
  searchQuery,
  searchType = "opp",
  children,
  shouldUpdateSearchForm = false,
  ...rest
}) => {
  const dispatch = useThunkDispatch();

  const history = useHistory();

  const queryString = React.useMemo(() => {
    return opportunityResultStore.util.opportunitySearchQueryToString(
      searchQuery
    );
  }, [searchQuery]);

  const action = React.useMemo(() => {
    switch (searchType) {
      case "org":
        return "/volunteering-organisations";
      case "favourites":
        return "/favourites";
      case "opp":
      default:
        return "/volunteering";
    }
  }, [searchType]);

  const href = React.useMemo(() => {
    switch (typeof to) {
      case "undefined":
        return history.createHref({
          pathname: action,
          search: queryString,
        });
      case "string":
        return to;
      default:
        return history.createHref(to);
    }
  }, [action, history, queryString, to]);

  const handleClick = React.useMemo(() => {
    return (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void => {
      e.preventDefault();

      history.push(href, {
        searchQuery,
      });

      if (searchType === "opp") {
        dispatch(
          opportunityResultStore.actions.fetchOpportunities(
            searchQuery as OpportunitySearchQueryState
          )
        ).then((data) => {
          history.replace(data.canonicalUrl, {
            searchQuery,
            shouldUpdateSearchForm,
          });
        });
        return;
      }

      if (searchType === "org") {
        dispatch(
          organisationResultStore.actions.fetchOrganisations(
            searchQuery as OrganisationSearchQueryState
          )
        ).then((data) => {
          history.replace(data.canonicalUrl, {
            searchQuery,
            shouldUpdateSearchForm,
          });
        });
        return;
      }

      if (searchType === "favourites") {
        /*
          Favourites is currently handled with a useEffect in it's own component
          so we don't do anything here.

          TODO : Look in to refactoring favourites so there's no ambiguity in the search flow
        */

        return;
      }
    };
  }, [
    dispatch,
    history,
    searchQuery,
    searchType,
    href,
    shouldUpdateSearchForm,
  ]);

  return (
    <a href={href} onClick={handleClick} {...rest}>
      {children}
    </a>
  );
};
